import { OptimizelyDecideOption, useDecision } from '@optimizely/react-sdk';
import { IMMEDIATE_FEATURES } from 'utils/experiment';
import SegmentUtils from 'utils/segment';
export const OPTIMIZELY_FEATURE_ON = 'on';
export const OPTIMIZELY_FEATURE_OFF = 'off';
export const OPTIMIZELY_VARIATION_ORIGINAL = 'original';
export const OPTIMIZELY_VARIATION_REFRESHED = 'refreshed';
export function useFeatureVariation(featureKey, { generateImpression } = {
    generateImpression: false,
}) {
    const decideOptions = [
        OptimizelyDecideOption.ENABLED_FLAGS_ONLY,
    ];
    if (!generateImpression) {
        decideOptions.push(OptimizelyDecideOption.DISABLE_DECISION_EVENT);
    }
    const [decision] = useDecision(featureKey, {
        autoUpdate: true,
        decideOptions,
    });
    return decision.enabled ? decision.variationKey : OPTIMIZELY_FEATURE_OFF;
}
/**
 * Returns the feature variation for the given feature key.
 * This function is for use in Class components or places where hooks are not usable.
 */
export const getFeatureVariation = (optimizely, featureKey, { generateImpression } = {
    generateImpression: false,
}) => {
    if (typeof window === 'undefined' || !optimizely) {
        return OPTIMIZELY_FEATURE_OFF;
    }
    const decideOptions = [
        OptimizelyDecideOption.ENABLED_FLAGS_ONLY,
        OptimizelyDecideOption.INCLUDE_REASONS,
    ];
    if (!generateImpression) {
        decideOptions.push(OptimizelyDecideOption.DISABLE_DECISION_EVENT);
    }
    const decision = optimizely.decide(featureKey, decideOptions);
    if (decision.enabled) {
        return decision.variationKey;
    }
    if (featureKey === IMMEDIATE_FEATURES.EXPRESS_CHECKOUT_WEB) {
        SegmentUtils.track({
            event: 'Test - Optimizely Decision For Bucketing',
            properties: decision,
            additionalOptions: {
                context: {
                    protocols: {
                        event_version: 1,
                    },
                },
            },
        });
    }
    return OPTIMIZELY_FEATURE_OFF;
};
/**
 * Returns the feature variation for the feature keys in IMMEDIATE_EXPERIMENTS.
 * This function is for use in Class components.
 */
export const getFeatureVariationsForKeys = (optimizely, keys) => {
    if (typeof window === 'undefined' || !optimizely) {
        return {};
    }
    const decideOptions = [
        OptimizelyDecideOption.ENABLED_FLAGS_ONLY,
        OptimizelyDecideOption.DISABLE_DECISION_EVENT,
        OptimizelyDecideOption.INCLUDE_REASONS,
    ];
    const decisions = keys.reduce((acc, key) => {
        acc[key] = optimizely.decide(key, decideOptions);
        return acc;
    }, {});
    const experimentValues = {};
    keys.forEach(experimentKey => {
        const decision = decisions[experimentKey];
        if (decision && decision.enabled) {
            experimentValues[experimentKey] = decision.variationKey;
        }
        else {
            experimentValues[experimentKey] = OPTIMIZELY_FEATURE_OFF;
        }
        if (decision &&
            experimentKey === IMMEDIATE_FEATURES.EXPRESS_CHECKOUT_WEB) {
            SegmentUtils.track({
                event: 'Test - Optimizely Decision For Reporting',
                properties: { ...decision, checkout_variant: 'Checkout' },
                additionalOptions: {
                    context: {
                        protocols: {
                            event_version: 1,
                        },
                    },
                },
            });
        }
        else if (!decision &&
            experimentKey === IMMEDIATE_FEATURES.EXPRESS_CHECKOUT_WEB) {
            SegmentUtils.track({
                event: 'Test - Optimizely Decision For Reporting',
                properties: {
                    enabled: null,
                    no_decision: true,
                    checkout_variant: 'Checkout',
                },
                additionalOptions: {
                    context: {
                        protocols: {
                            event_version: 1,
                        },
                    },
                },
            });
        }
    });
    return experimentValues;
};
